// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook for fetching and managing bulk item inventory data.
 * This module provides functionality for requesting and caching bulk item
 * inventory data from the remote API using React Query.
 */
import { getBulkItemsInventory } from '@/inventory/api/get-bulk-items-inventory';
import {
  BulkItemsInventoryResponse,
  InventoryFilters,
} from '@/inventory/api/types';
import {
  normalizeFilters,
  serializeFiltersToKey,
} from '@/inventory/utilities/serialize-filters';
import { THIRTY_SECONDS_MS } from '@/utilities/time-constants';
import { UseQueryResult, useQuery } from '@tanstack/react-query';

/**
 * Configuration options for the useItemInventory hook.
 */
interface UseItemInventoryOptions {
  filters?: InventoryFilters;
  enabled?: boolean;
}

/**
 * Return type for the useItemInventory hook.
 */
interface UseItemInventoryResult {
  /**
   * The item inventory response data containing the list of items with quantities and locations
   */
  itemInventory?: BulkItemsInventoryResponse;

  /**
   * Whether the initial data is being loaded
   */
  isLoading: boolean;

  /**
   * Whether a background fetch is in progress
   */
  isFetching: boolean;

  /**
   * Whether the query has encountered an error
   */
  isError: boolean;

  /**
   * The error object if the query failed
   */
  error: Error | null;

  /**
   * Function to manually trigger a refetch
   */
  refetch: UseQueryResult<BulkItemsInventoryResponse>['refetch'];
}

/**
 * A React hook that fetches and caches bulk item inventory data from the remote API.
 * This hook fetches bulk items (isPhysical=true, hasAssets=false) with their current
 * inventory quantities and locations.
 *
 * The hook supports:
 * - Optional filtering by item key, location ID
 * - Automatic background updates and caching via React Query
 * - Manual refetch capability
 * - Loading and error state management
 *
 * @param options - Configuration options for filtering and query behavior
 * @returns Object containing the item inventory data and query state
 *
 * @example
 * ```tsx
 * // Fetch all bulk item inventory
 * const { itemInventory, isLoading } = useItemInventory();
 *
 * // Fetch inventory filtered by specific items
 * const { itemInventory, isLoading } = useItemInventory({
 *   filters: { itemKeys: ['item-123', 'item-456'] }
 * });
 *
 * // Conditionally fetch based on some condition
 * const { itemInventory, isLoading } = useItemInventory({
 *   enabled: shouldFetch
 * });
 * ```
 */
export function useItemInventory(
  options: UseItemInventoryOptions = {}
): UseItemInventoryResult {
  const { filters, enabled = true } = options;

  // Normalize filters and create a stable query key
  const normalizedFilters = normalizeFilters(filters);
  const filtersKey = serializeFiltersToKey(normalizedFilters);

  const queryResult = useQuery({
    queryKey: ['inventory', 'items', filtersKey],
    queryFn: () => getBulkItemsInventory(normalizedFilters),
    enabled,
    staleTime: 0, // Always stale - refetch immediately on mount/access
    refetchInterval: THIRTY_SECONDS_MS,
    refetchOnWindowFocus: true,
  });

  return {
    itemInventory: queryResult.data,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
}
